import React, { useEffect, useRef, useState } from "react";
import { AcsContext, AcsOngoingCallContext } from "./AcsContext";
import {
	OnBehalfOfOptions,
	StartTeamsGroupCallOptions,
	TeamsCall,
	TeamsCallAgent,
	TeamsIncomingCall,
} from "@azure/communication-calling";
import {
	getIncomingCallAgent,
	getOutgoingCallAgent,
	initializeCallAgent,
} from "@/common/acs/acsService";
import { embededInTeams } from "@/common/authentication/authenticationUtils";
import { useAppSettingsStore } from "@/common/stores/persistingStores/app/appSettingsStore";
import { AppSettingsState } from "@/common/stores/persistingStores/app";
import { captureError } from "@/common/helpers/sentryHelper";
import { useMessage } from "@/common/messages";
import {
	AcsClientType,
	AcsErrorCode,
	PlatformType,
} from "@/common/constants/shared";
import { MicrosoftTeamsAppIdentifier } from "@azure/communication-common";
import { useIntl } from "react-intl";
import { useAuth } from "@/hooks/authentication";
import {
	OngoingCall,
	OutboundCallRequest,
} from "@/repository/models/outboundCall";
import PlaySoundHelper, { SoundType } from "../helpers/play-sound-helper";
import { CallState } from "../constants/callSession";
import {
	AcsTokenError,
	EntraTokenCredentialError,
	MsalAuthError,
} from "./error";
import { StorageKeys } from "../constants/storageKeys";
import {
	fetchCurrentCallSession,
	searchContactsById,
} from "@/repository/queryFns";
import { InteractionDirection } from "@/repository/models";
import { CallSession } from "../models/callSession";
import { InteractionType } from "../types";
import {
	CurrentInteractionState,
	useCurrentInteractionStore,
} from "../stores/nonpersistingStores/currentInteraction";
import { CallSessionActions, useCallSessionStore } from "../stores/nonpersistingStores/callSession";

export const AcsProvider: React.FC<{ children: React.ReactNode }> = ({
	children,
}) => {
	const [incomingCall, setIncomingCall] = useState<TeamsIncomingCall | null>(
		null
	);

	const [ongoingCalls, setOngoingCalls] = useState<Map<string, OngoingCall>>(
		new Map()
	);

	const agentIcomingCallsRef = useRef<TeamsCallAgent | null>(null);
	const agentOutgoingCallsRef = useRef<TeamsCallAgent | null>(null);
	const initializedRef = useRef(false);
	const callAgentConnectedRef = useRef(false);
	const notificationRef = useRef<Notification | null>(null);
	const audioFilePlaying = useRef<boolean>(false);
	const [callerInfo, setCallerInfo] = useState<string>("");

	const appSettings = useAppSettingsStore(
		(state: AppSettingsState) => state.settings
	);
	const { show: showMessage } = useMessage();
	const intl = useIntl();
	const authContext = useAuth();

	useEffect(() => {
		if (
			!embededInTeams &&
			"Notification" in window &&
			Notification.permission !== "granted"
		) {
			Notification.requestPermission();
		}

		return () => {
			PlaySoundHelper.stopPlayingLoop();
		};
	}, []);

	useEffect(() => {
		if (!authContext.isLogged) {
			agentIcomingCallsRef.current = null;
			agentOutgoingCallsRef.current = null;
			initializedRef.current = false;
		}
	}, [authContext.isLogged]);

	useEffect(() => {
		if (initializedRef.current || !authContext.isLogged) {
			return;
		}

		initAcsCallAgents();
	}, [appSettings]);

	const currentInteraction = useCurrentInteractionStore(
		(state: CurrentInteractionState) => state.active
	);

	const updateCallSession = useCallSessionStore(
		(actions: CallSessionActions) => actions.update
	);

	const showIncomingCallNotification = (id: string, callerName: string) => {
		if (
			document.visibilityState === "visible" ||
			Notification.permission !== "granted"
		) {
			return;
		}

		notificationRef.current?.close();
		const message = intl.formatMessage({
			id: "Interaction.Contents.CallMessage",
		});

		const notification = new Notification(callerName, {
			body: message,
			icon: "/logo192.png",
			tag: `${id}-${Date.now()}`,
			requireInteraction: true,
			silent: false,
		});

		notification.onclick = () => {
			window.focus();
			notification.close();
		};

		notification.onclose = () => {
			notificationRef.current = null;
		};

		notificationRef.current = notification;
	};

	const getErrorMessageId = (error: unknown): string => {
		if (error instanceof MsalAuthError) {
			return "ErrorMessage.MsalAuthFailed";
		} else if (error instanceof AcsTokenError) {
			return "ErrorMessage.AcsTokenExchangeFailed";
		} else if (error instanceof EntraTokenCredentialError) {
			return "ErrorMessage.EntraAuthFailed";
		}

		return "ErrorMessage.AcsInitError"; // fallback
	};

	const initAcsCallAgents = async () => {
		if (embededInTeams) {
			return; // disable until we support Unify calls when embeded in Teams
		}

		if (
			!appSettings ||
			!appSettings.acsClientType ||
			!appSettings.acsResourceEndpointUrl
		) {
			return; // No ACS client type configured
		}

		initializedRef.current = true;

		try {
			await initializeCallAgent(appSettings);

			const callAgent: TeamsCallAgent | null = getIncomingCallAgent();

			if (!callAgent) {
				showMessage("workspace", {
					intent: "warning",
					text: intl.formatMessage({ id: "ErrorMessage.AcsInitError" }),
				});

				return;
			}

			if (appSettings.acsClientType !== AcsClientType.Teams) {
				callAgent.on("incomingCall", (args: any) => {
					const incomingCallData: TeamsIncomingCall = args.incomingCall;

					if (!incomingCallData) {
						return;
					}

					const callerName =
						incomingCallData.callerInfo.displayName ?? "CC4Teams";

					incomingCallData.on("callEnded", () => {
						setCallerInfo("");
						setIncomingCall(null);
					});

					setCallerInfo(callerName);
					setIncomingCall(incomingCallData);

					showIncomingCallNotification(incomingCallData.id, callerName);
				});
			}

			callAgent.on("connectionStateChanged", async () => {
				const state = callAgent?.connectionState;

				if (state === CallState.Connected) {
					callAgentConnectedRef.current = true;
				}

				if (state === CallState.Disconnected && callAgentConnectedRef.current) {
					callAgentConnectedRef.current = false;

					initAcsCallAgents();
				}
			});

			agentIcomingCallsRef.current = callAgent;
			agentOutgoingCallsRef.current = getOutgoingCallAgent();
		} catch (error: any) {
			showMessage("workspace", {
				intent: "warning",
				text: intl.formatMessage({ id: getErrorMessageId(error) }),
			});

			captureError(error, "initAcsCallAgents failed");
		}
	};

	const attachCallListeners = (call: TeamsCall, ringingFilePath?: string) => {
		call.on("stateChanged", () => {
			if (call.state !== CallState.Ringing && audioFilePlaying.current) {
				audioFilePlaying.current = false;
				PlaySoundHelper.stopPlayingLoop();
			}

			if (call.state === CallState.Connected && ongoingCalls?.size == 0) {
				// get call session, as sometimes action buttons are not updated correctly
				fetchCurrentCallSession().then((callSession: CallSession) => {
					if (
						!currentInteraction ||
						(currentInteraction.type === InteractionType.Call &&
							callSession?.id === currentInteraction.id)
					) {
						updateCallSession(callSession);
					}
				});
			}

			if (call.state === CallState.Disconnected) {
				setOngoingCalls((prev) => {
					const updated = new Map(prev);
					updated.delete(call.id);

					return updated;
				});

				if (call.callEndReason?.code !== 0) {
					if (call.callEndReason?.code === AcsErrorCode.Declined) {
						showMessage("workspace", {
							intent: "error",
							text: intl.formatMessage({
								id: "ErrorMessage.Declined",
							}),
						});

						throw new Error("CallDeclined");
					}

					if (call.callEndReason?.subCode === AcsErrorCode.MissingPolicy) {
						showMessage("workspace", {
							intent: "error",
							text: intl.formatMessage({
								id: "ErrorMessage.EnterpriseVoicePolicyMissing",
							}),
						});

						throw new Error("EnterpriseVoicePolicyMissing");
					}

					showMessage("workspace", {
						intent: "error",
						text: intl.formatMessage({
							id: "ErrorMessage.Unavailable",
						}),
					});
				}

				call.dispose();
			}

			if (call.state === CallState.Ringing && ringingFilePath) {
				const ringingAudioFile = PlaySoundHelper.playSound(
					SoundType.Ringing,
					ringingFilePath,
					true
				);

				if (ringingAudioFile) {
					audioFilePlaying.current = true;
				}
			}
		});
	};

	const startCall = async (data: OutboundCallRequest) => {
		if (!authContext.isLogged || data.platformType !== PlatformType.Acs) {
			return; // not an ACS endpoint
		}

		if (
			!appSettings ||
			!appSettings.acsClientType ||
			!appSettings.acsResourceEndpointUrl ||
			appSettings.acsClientType === AcsClientType.Teams
		) {
			return; // No ACS client type configured or call should be started server side in MS Teams
		}

		try {
			const agent = agentOutgoingCallsRef.current;
			if (!agent) {
				console.warn("Call agent not initialized");

				return;
			}

			const onBehalfOfApp: MicrosoftTeamsAppIdentifier = {
				teamsAppId: data.selectedEndpoint.objectId,
			};

			const onBehalfOfOptions: OnBehalfOfOptions = { userId: onBehalfOfApp };

			const options: StartTeamsGroupCallOptions = {
				onBehalfOfOptions,
				customContext: {
					sipHeaders: [
						{
							key: "ScenarioId",
							value: data.scenarioId || crypto.randomUUID(),
						},
					],
				},
			};

			let targetIdentifier: any;
			if (data.target.isPstn) {
				targetIdentifier = { phoneNumber: data.target.target };
			} else if (
				appSettings.acsClientType === AcsClientType.CCaaS &&
				data.isAgent
			) {
				targetIdentifier = {
					userId: data.target.contactId || data.target.target,
					tenantId: localStorage.getItem(StorageKeys.TIDForAzure) as string,
					resourceId: appSettings.acsResourceId,
					cloud: "public",
				};
			} else {
				targetIdentifier = {
					microsoftTeamsUserId: data.target.contactId || data.target.target,
					cloud: "public",
				};
			}

			const call = agent.startCall(targetIdentifier, options);

			attachCallListeners(call, data.ringingFilePath);

			setOngoingCalls((prev) => {
				const updated = new Map(prev);
				updated.set(call.id, {
					call: call,
					name:
						data.target.contactName ??
						intl.formatMessage({ id: "Common.Unknown" }),
					info: data.target.target,
					date: new Date(),
					direction: InteractionDirection.Outbound,
					isCCaaSCall: true,
				});

				return updated;
			});
		} catch (error: any) {
			captureError(error, "startCall failed");

			showMessage("workspace", {
				intent: "error",
				text: intl.formatMessage({ id: "Interaction.New.Failed" }),
			});
		}
	};

	const answerCall = async () => {
		try {
			if (incomingCall) {
				const answeredCall = await incomingCall.accept();

				attachCallListeners(answeredCall, undefined);

				let info = "";
				let userId = "";
				let isCCaaSCall = true;
				const identifier = answeredCall.callerInfo.identifier;

				if (identifier && "kind" in identifier) {
					switch (identifier.kind) {
						case "microsoftTeamsUser":
							userId = identifier.microsoftTeamsUserId;
							isCCaaSCall = false;
							break;
						case "teamsExtensionUser":
							userId = identifier.userId;
							break;
						case "phoneNumber":
							info = identifier.phoneNumber;
							isCCaaSCall = false;
							break;
					}

					const contactInfo = await searchContactsById(userId);
					if (contactInfo) {
						info = contactInfo.email || contactInfo.userPrincipalName || "";
					}
				}

				setOngoingCalls((prev) => {
					const updated = new Map(prev);
					updated.set(answeredCall.id, {
						call: answeredCall,
						name:
							answeredCall.callerInfo.displayName ??
							intl.formatMessage({ id: "Common.Unknown" }),
						info: info,
						date: new Date(),
						direction: InteractionDirection.Incoming,
						isCCaaSCall: isCCaaSCall,
					});

					return updated;
				});
			}
		} catch (error: any) {
			captureError(error, "answerCall failed");
		}

		setIncomingCall(null);
	};

	const rejectCall = async () => {
		try {
			if (incomingCall) {
				await incomingCall.reject();
			}
		} catch (error: any) {
			captureError(error, "rejectCall failed");
		}

		setIncomingCall(null);
	};

	const hangUpCall = async (callId: string) => {
		try {
			const ongoing = ongoingCalls.get(callId);

			if (ongoing) {
				await ongoing.call.hangUp({
					forEveryone: ongoing.call.remoteParticipants.length <= 1,
				}); // hang up for everyone if it's a 1:1 call

				ongoing.call.dispose();

				setOngoingCalls((prev) => {
					const updated = new Map(prev);
					updated.delete(callId);

					return updated;
				});
			}
		} catch (error: any) {
			captureError(error, "hangUpCall failed");

			showMessage("workspace", {
				intent: "warning",
				text: intl.formatMessage({ id: "ErrorMessage.HangUp" }),
			});
		}
	};

	const muteCall = async (callId: string) => {
		try {
			const ongoing = ongoingCalls.get(callId);

			if (ongoing) {
				await ongoing.call.mute();
			}
		} catch (error: any) {
			captureError(error, "muteCall failed");

			showMessage("workspace", {
				intent: "warning",
				text: intl.formatMessage({ id: "ErrorMessage.Mute" }),
			});
		}
	};

	const unmuteCall = async (callId: string) => {
		try {
			const ongoing = ongoingCalls.get(callId);

			if (ongoing) {
				await ongoing.call.unmute();
			}
		} catch (error: any) {
			captureError(error, "unmuteCall failed");

			showMessage("workspace", {
				intent: "warning",
				text: intl.formatMessage({ id: "ErrorMessage.Unmute" }),
			});
		}
	};

	const muteAllRemoteParticipants = async (callId: string) => {
		try {
			const ongoing = ongoingCalls.get(callId);

			if (ongoing) {
				await ongoing.call.muteAllRemoteParticipants();
			}
		} catch (error: any) {
			captureError(error, "unmuteCall failed");

			showMessage("workspace", {
				intent: "warning",
				text: intl.formatMessage({ id: "ErrorMessage.MuteAll" }),
			});
		}
	};

	const holdCall = async (callId: string) => {
		try {
			const ongoing = ongoingCalls.get(callId);

			if (ongoing) {
				await ongoing.call.hold();
			}
		} catch (error: any) {
			captureError(error, "holdCall failed");

			showMessage("workspace", {
				intent: "warning",
				text: intl.formatMessage({ id: "ErrorMessage.Hold" }),
			});
		}
	};

	const unholdCall = async (callId: string) => {
		try {
			const ongoing = ongoingCalls.get(callId);

			if (ongoing) {
				await ongoing.call.resume();
			}
		} catch (error: any) {
			captureError(error, "unholdCall failed");

			showMessage("workspace", {
				intent: "warning",
				text: intl.formatMessage({ id: "ErrorMessage.Unhold" }),
			});
		}
	};

	return (
		<AcsContext.Provider
			value={{
				incomingCall,
				ongoingCalls,
				callerInfo,
				startCall,
				answerCall,
				rejectCall,
				hangUpCall,
				muteCall,
				muteAllRemoteParticipants,
				unmuteCall,
				holdCall,
				unholdCall,
			}}
		>
			<AcsOngoingCallContext.Provider value={ongoingCalls.size > 0}>
				{children}
			</AcsOngoingCallContext.Provider>
		</AcsContext.Provider>
	);
};
